import React, { useCallback, useEffect, useRef, useState } from 'react';

import { getIpdDepositsReport, openIpdDepositsReportPdf } from '../../services/api/ipdReportsApi';
import { PAYMENT_MODE_LABELS, DEPOSITS_REPORT_DEPT_SCOPED_COPY } from '../../constants/admission';
import { formatPaise, todayYmd } from '../../utils/money';
import { backdatedDischargeTag } from './backdatedCompletions';

const overlayCls = 'fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4';
const panelCls = 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl w-full max-w-4xl max-h-[92vh] overflow-y-auto';
const btnPrimary = 'px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-semibold disabled:opacity-60';
const btnSecondary = 'px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm';

/** Long-list bound (mirrors the same "show first N + expander" pattern the running-bill/deposits tables use). */
const VISIBLE_ROWS = 25;

/**
 * IpdDepositsReportDialog (day-1 S12, ADDENDUM-3 §2/F-FE) — "Today's IPD
 * report": every admitted-patient deposit collected today (§2.8-adjacent
 * frozen contract `GET /reports/ipd-deposits`), + a one-tap Print (the PDF
 * rail, same blob + in-app FileViewerStore pattern as the S3 invoice print).
 * Gated by the caller on the `VIEW_IPD_REVENUE` permission (RBAC-IPD FE-1;
 * `REPORT_PERMISSIONS.VIEW_IPD_REVENUE`). Fetched ONCE per mount
 * (ref-guarded single-flight); no date picker — defaults to org-local today
 * per the story. Shows a "Showing your departments only" line when (and only
 * when) the response carries `dept_scoped: true` (admission-access S8-AC3).
 */
export default function IpdDepositsReportDialog({ onClose }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [showAllRefunds, setShowAllRefunds] = useState(false);
  const [printing, setPrinting] = useState(false);
  const fetchedRef = useRef(false);

  const date = todayYmd();

  const reload = useCallback(async () => {
    const res = await getIpdDepositsReport(date);
    if (res.success) { setReport(res.data); setError(null); }
    else setError(res.error || 'Failed to load the deposits report.');
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    reload();
  }, [reload]);

  const handlePrint = useCallback(async () => {
    setPrinting(true);
    await openIpdDepositsReportPdf(date);
    setPrinting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = report?.rows || [];
  const visibleRows = showAll ? rows : rows.slice(0, VISIBLE_ROWS);
  const hiddenCount = rows.length - visibleRows.length;

  const refunds = report?.refunds || [];
  const refundsTotalPaise = report?.refunds_total_paise || 0;
  // Net = collected − refunded (may be NEGATIVE). Prefer the frozen server field;
  // derive for legacy envelopes that predate `net_deposit_paise`.
  const netDepositPaise = typeof report?.net_deposit_paise === 'number'
    ? report.net_deposit_paise
    : ((report?.day_total_paise || 0) - refundsTotalPaise);
  const visibleRefunds = showAllRefunds ? refunds : refunds.slice(0, VISIBLE_ROWS);
  const hiddenRefundCount = refunds.length - visibleRefunds.length;

  return (
    <div className={overlayCls} data-testid="ipd-deposits-report-dialog">
      <div className={panelCls}>
        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center gap-2 flex-wrap">
          <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Today&rsquo;s IPD report</h3>
          <span className="text-[12px] text-slate-500 dark:text-slate-400">{date}</span>
          <button
            type="button"
            className={`${btnPrimary} ml-auto`}
            onClick={handlePrint}
            disabled={printing || loading || !!error}
            data-testid="ipd-deposits-print"
          >
            {printing ? 'Opening…' : 'Print'}
          </button>
          <button type="button" onClick={onClose} className="text-slate-500 dark:text-slate-400" aria-label="Close">✕</button>
        </div>

        <div className="p-4">
          {report?.dept_scoped === true && (
            <div className="text-[12px] text-slate-500 dark:text-slate-400 mb-3" data-testid="ipd-deposits-dept-scoped">{DEPOSITS_REPORT_DEPT_SCOPED_COPY}</div>
          )}
          {loading ? (
            <div data-testid="ipd-deposits-loading">
              {[40, 85, 78].map((w, i) => (
                <div key={i} className="h-3 rounded bg-slate-200 dark:bg-slate-700 animate-pulse mb-2.5" style={{ width: `${w}%` }} />
              ))}
              <div className="text-[12px] text-slate-500 dark:text-slate-400 mt-2">Loading today&rsquo;s report…</div>
            </div>
          ) : error ? (
            <div className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12.5px] p-3" data-testid="ipd-deposits-error">{error}</div>
          ) : rows.length === 0 && refunds.length === 0 ? (
            <div className="p-6 text-center text-[12.5px] text-slate-500 dark:text-slate-400" data-testid="ipd-deposits-empty">No deposits collected today.</div>
          ) : (
            <>
              {rows.length > 0 && (
                <>
                  <div className="flex gap-2.5 px-2 py-1.5 text-[10.5px] font-bold uppercase tracking-wide text-slate-400 dark:text-slate-500 border-b border-slate-200 dark:border-slate-700">
                    <span className="flex-1">Patient</span>
                    <span className="w-28">IP no.</span>
                    <span className="w-32">Ward / Bed</span>
                    <span className="w-24 text-right">Amount</span>
                    <span className="w-20">Method</span>
                    <span className="w-20">Receipt</span>
                    <span className="w-32">Collected by</span>
                  </div>
                  {visibleRows.map((row) => (
                    <div key={row.admission_id + row.receipt_number} className="flex gap-2.5 px-2 py-2 text-[13px] border-b border-slate-100 dark:border-slate-800" data-testid={`ipd-deposits-row-${row.admission_id}`}>
                      <span className="flex-1 min-w-0 truncate text-slate-900 dark:text-white">
                        {row.patient_name}
                        {backdatedDischargeTag(row) && <span className="block text-[11px] text-amber-700 dark:text-amber-400" data-testid="backdated-discharge-tag">{backdatedDischargeTag(row)}</span>}
                      </span>
                      <span className="w-28 text-slate-600 dark:text-slate-300">{row.ip_number}</span>
                      <span className="w-32 text-slate-600 dark:text-slate-300">{[row.ward_name, row.bed_label].filter(Boolean).join(' · ') || '—'}</span>
                      <span className="w-24 text-right font-semibold tabular-nums text-slate-900 dark:text-white">{formatPaise(row.amount_paise)}</span>
                      <span className="w-20 text-slate-600 dark:text-slate-300">{PAYMENT_MODE_LABELS[row.payment_mode] || row.payment_mode}</span>
                      <span className="w-20 text-slate-600 dark:text-slate-300">{row.receipt_number ?? '—'}</span>
                      <span className="w-32 truncate text-slate-600 dark:text-slate-300">{row.collected_by_name}</span>
                    </div>
                  ))}
                  {hiddenCount > 0 && (
                    <button type="button" onClick={() => setShowAll(true)} className="w-full mt-2 text-center px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-[12.5px] font-medium hover:bg-slate-100 dark:hover:bg-slate-700" data-testid="ipd-deposits-expander">
                      Show {hiddenCount} more
                    </button>
                  )}
                </>
              )}

              {refunds.length > 0 && (
                <div className={rows.length > 0 ? 'mt-5' : ''}>
                  <div className="text-[12px] font-bold uppercase tracking-wide text-amber-700 dark:text-amber-400 mb-1.5" data-testid="ipd-refunds-header">
                    Deposits refunded today
                  </div>
                  <div className="flex gap-2.5 px-2 py-1.5 text-[10.5px] font-bold uppercase tracking-wide text-slate-400 dark:text-slate-500 border-b border-slate-200 dark:border-slate-700">
                    <span className="flex-1">Patient</span>
                    <span className="w-28">IP no.</span>
                    <span className="w-32">Ward / Bed</span>
                    <span className="w-24 text-right">Amount</span>
                    <span className="w-20">Method</span>
                    <span className="w-20">Receipt</span>
                    <span className="w-32">Refunded by</span>
                  </div>
                  {visibleRefunds.map((row, i) => (
                    <div key={`${row.admission_id}-refund-${row.receipt_number ?? i}`} className="flex gap-2.5 px-2 py-2 text-[13px] border-b border-slate-100 dark:border-slate-800" data-testid={`ipd-refunds-row-${row.admission_id}`}>
                      <span className="flex-1 min-w-0 truncate text-slate-900 dark:text-white">
                        {row.patient_name}
                        {backdatedDischargeTag(row) && <span className="block text-[11px] text-amber-700 dark:text-amber-400" data-testid="backdated-discharge-tag">{backdatedDischargeTag(row)}</span>}
                      </span>
                      <span className="w-28 text-slate-600 dark:text-slate-300">{row.ip_number}</span>
                      <span className="w-32 text-slate-600 dark:text-slate-300">{[row.ward_name, row.bed_label].filter(Boolean).join(' · ') || '—'}</span>
                      <span className="w-24 text-right font-semibold tabular-nums text-amber-700 dark:text-amber-400">{formatPaise(-row.amount_paise)}</span>
                      <span className="w-20 text-slate-600 dark:text-slate-300">{PAYMENT_MODE_LABELS[row.payment_mode] || row.payment_mode}</span>
                      <span className="w-20 text-slate-600 dark:text-slate-300">{row.receipt_number ?? '—'}</span>
                      <span className="w-32 truncate text-slate-600 dark:text-slate-300">{row.collected_by_name}</span>
                    </div>
                  ))}
                  {hiddenRefundCount > 0 && (
                    <button type="button" onClick={() => setShowAllRefunds(true)} className="w-full mt-2 text-center px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-[12.5px] font-medium hover:bg-slate-100 dark:hover:bg-slate-700" data-testid="ipd-refunds-expander">
                      Show {hiddenRefundCount} more
                    </button>
                  )}
                </div>
              )}

              {rows.length > 0 && (
                <div className="flex justify-between items-center mt-3 pt-3 border-t border-slate-300 dark:border-slate-600 text-[14px] font-extrabold text-slate-900 dark:text-white">
                  <span>Day total ({report.count} deposit{report.count === 1 ? '' : 's'})</span>
                  <span className="tabular-nums" data-testid="ipd-deposits-day-total">{formatPaise(report.day_total_paise)}</span>
                </div>
              )}
              {refunds.length > 0 && (
                <div className="flex justify-between items-center mt-2 pt-2 border-t border-amber-300 dark:border-amber-700 text-[14px] font-extrabold text-amber-700 dark:text-amber-400">
                  <span>Deposit refunded ({refunds.length} refund{refunds.length === 1 ? '' : 's'})</span>
                  <span className="tabular-nums" data-testid="ipd-deposits-refund-total">{formatPaise(-refundsTotalPaise)}</span>
                </div>
              )}
              <div className="flex justify-between items-center mt-2 pt-2.5 border-t-2 border-slate-800 dark:border-slate-100 text-[15px] font-extrabold text-slate-900 dark:text-white">
                <span>Today&rsquo;s Net Deposit</span>
                <span className="tabular-nums" data-testid="ipd-deposits-net-total">{formatPaise(netDepositPaise)}</span>
              </div>
            </>
          )}
        </div>
        <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end">
          <button type="button" className={btnSecondary} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
